import React, { Fragment, useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';

import HeaderV2 from '../../components/headerV2/HeaderV2';
import FooterV2 from '../../components/footerV2/FooterV2';
import AboutHero from '../../components/aboutHero/AboutHero';
import useScrollReveal from '../../hooks/useScrollReveal';

import Focused from '../../images/about/focused.webp';
import CommunityPartnership from '../../images/about/community-partnership.webp';
import Honesty from '../../images/about/honesty.webp';
import EasyToJoin from '../../images/about/easy-to-join.webp';

import metadataJSON from "../../data/metadata.json";
import schemadataJSON from "../../data/schemadata.json";
import './aboutV2.css';

/* Camp photo supplied in the Sep 2026 website review (lives in /public). */
const OurJourneyImg = process.env.PUBLIC_URL + "/images/about-v2/our-journey.jpg";

const reasons = [
    {
        icon: Focused,
        alt: 'Focused projects icon',
        title: 'Focused Projects',
        text: "Our initiatives solve real-world problems - beginning with India's most urgent preventable crisis: the shortage of emergency blood.",
    },
    {
        icon: CommunityPartnership,
        alt: 'Community collaborations icon',
        title: 'Community Collaborations',
        text: 'We work closely with local communities, Panchayats, hospitals, universities, and NGOs to ensure our efforts are grounded in actual need.',
    },
    {
        icon: Honesty,
        alt: 'Honesty and transparency icon',
        title: 'Honesty and Transparency',
        text: 'We operate with integrity in everything we do, keeping all actions accountable to our donors, partners, and the communities we serve.',
    },
    {
        icon: EasyToJoin,
        alt: 'Easy to join icon',
        title: 'Easy to Join',
        text: 'We welcome everyone - whether you want to donate blood, register as a Life Saver Army volunteer, make a financial contribution, or simply spread the word.',
    },
];

const OurJourney = ({ path }) => {

    // Setting metadata and schemadata
    const [metadata, setMetadata] = useState({ title: "JJFIndia | About us" });
    const [schemadata, setSchemadata] = useState({});

    const rootRef = useRef(null);
    useScrollReveal(rootRef);

    // Getting the schemadata and setting the schema data.
    useEffect(() => {
        let tempMetadata = metadataJSON[path];
        let tempSchemadata = schemadataJSON[path];

        if (tempMetadata) {
            setMetadata(tempMetadata);
        } else {
            tempMetadata = {
                title: "JJFIndia | About us",
                description: "JJFIndia About us page",
                keywords: "",
                robots: "index, follow",
                canonical: "https://jjfindia.com/about-us/our-journey",
                openGraph: {
                    title: "JJFIndia | About us",
                    description: "JJFIndia About us page",
                    url: "https://jjfindia.com/about-us/our-journey"
                },
                twitter: {
                    card: "summary_large_image",
                    title: "JJFIndia | About us",
                    description: "JJFIndia About us page"
                }
            }

            setMetadata(tempMetadata);
        }

        if (tempSchemadata) {
            setSchemadata(tempSchemadata);
        }

    }, [path]);

    return (
        <Fragment>
            <Helmet>
                <link rel="preconnect" href="https://fonts.googleapis.com" />
                <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
                <link
                    href="https://fonts.googleapis.com/css2?family=Anton&family=Inter:wght@400;500;600;700;800&display=swap"
                    rel="stylesheet"
                />
                <title>{metadata.title}</title>
                <meta name="description" content={metadata.description} />
                <meta name="keywords" content={metadata.keywords} />
                <meta name="robots" content={metadata.robots} />
                <link rel="canonical" href={metadata.canonical} />
                <meta property="og:title" content={metadata.openGraph?.title} />
                <meta property="og:description" content={metadata.openGraph?.description} />
                <meta property="og:url" content={metadata.canonical} />
                <meta name="twitter:title" content={metadata.twitter?.title} />
                <meta name="twitter:description" content={metadata.twitter?.description} />
                <script type="application/ld+json">{JSON.stringify(schemadata, null, 2)}</script>
            </Helmet>

            <div className="jjfv2 about-live" ref={rootRef}>
                <HeaderV2 />

                <main className="page_content">
                    {/* ---------- Page hero ---------- */}
                    <AboutHero
                        eyebrow="How it all began"
                        title="Our Journey"
                        crumbs={[
                            { label: 'Home', to: '/' },
                            { label: 'About Us', to: '/about-us/who-we-are' },
                            { label: 'Our Journey' },
                        ]}
                    />

                    {/* ---------- Our journey: intro ---------- */}
                    <section className="section section--mist about" id="journey-intro">
                        <div className="wrap split">
                            <div className="about-media reveal">
                                <span className="leaf-frame" aria-hidden="true"></span>
                                <div className="ph">
                                    <img src={OurJourneyImg} alt="Donors at a Life Saver Army blood donation camp" loading="lazy" />
                                </div>
                            </div>
                            <div className="about-copy reveal d1">
                                <h2>From One Idea to a Growing Network</h2>
                                <p className="lead">
                                    What began as a small effort to organise willing hands has grown into a
                                    network spanning multiple states. This page traces how it came about — the
                                    first camp, the first partnership, the first life saved.
                                </p>
                            </div>
                        </div>
                    </section>

                    {/* ---------- Journey narrative ---------- */}
                    <section className="section">
                        <div className="wrap">
                            <div className="about-head">
                                <h2>A Journey of Commitment, Compassion and Dedication</h2>
                            </div>
                            <div className="about-prose about-prose--split reveal">
                                <div>
                                <p>
                                    Since our founding, Jainam Jivika Foundation has been committed to driving
                                    positive change across India. Our journey has been shaped by the conviction
                                    that social impact must be practical, community-rooted, and built on trust.
                                </p>
                                <p>
                                    From early outreach at Varanasi universities to expansion across nine Indian
                                    states, our flagship initiative; Life Saver Army has grown from a vision
                                    into a functioning nationwide blood donor network.
                                </p>
                                </div>

                                <div className="about-callout">
                                    <span className="about-callout__badge">
                                        <i className="fa-solid fa-hand-holding-heart"></i>
                                        Powered by people
                                    </span>
                                    <p>
                                        None of this would be possible without the unwavering support of our
                                        donors, volunteers, field coordinators, hospital partners, and community
                                        leaders, who work together every day with one shared belief: that giving
                                        blood is one of the most powerful acts of compassion a person can make.
                                    </p>
                                </div>

                                <div className="about-chips about-chips--line">
                                    <span className="about-chip">
                                        <i className="fa-solid fa-location-dot"></i>
                                        9 Indian States
                                    </span>
                                    <span className="about-chip">
                                        <i className="fa-solid fa-droplet"></i>
                                        Voluntary Donor Network
                                    </span>
                                    <span className="about-chip">
                                        <i className="fa-solid fa-users"></i>
                                        Hospital &amp; Community Partners
                                    </span>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* ---------- Why choose us ---------- */}
                    <section className="section section--mist" id="feature">
                        <div className="wrap">
                            <div className="about-head">
                                <span className="eyebrow">Choosing Jainam Jivika Foundation?</span>
                                <h2>Why Choose Us?</h2>
                            </div>
                            <div className="ways">
                                {reasons.map((reason, index) => (
                                    <article className={"way reveal" + (index ? " d" + index : "")} key={index}>
                                        <div className="wic">
                                            <img src={reason.icon} alt={reason.alt} loading="lazy" />
                                        </div>
                                        <h3>{reason.title}</h3>
                                        <p>{reason.text}</p>
                                    </article>
                                ))}
                            </div>
                            <p className="lead reveal" style={{ maxWidth: '60ch', margin: '44px auto 0', textAlign: 'center' }}>
                                By choosing Jainam Jivika Foundation, you become part of a community that brings
                                hope and opportunity to those who need it most. Together, we can create a better
                                and brighter future for everyone.
                            </p>
                        </div>
                    </section>
                </main>

                <FooterV2 />
            </div>
        </Fragment>
    )
};

export default OurJourney;
